import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link2, Share2 } from "lucide-react";
import Compass from "./Compass";

const SHARE_TEXT =
  "I just spun the MarStar compass and unlocked a discount — choose your direction:";

export default function Intro({ spun, onComplete }) {
  const [canSpin, setCanSpin] = useState(!spun);
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const pick = (dir) => {
    if (locked) return;
    setSelected(dir);
    setLocked(true);
    setTimeout(() => onComplete(dir), 1700);
  };

  const share = (where) => {
    const url = window.location.origin;
    const links = {
      x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${SHARE_TEXT} ${url}`)}`,
    };
    if (where === "copy") {
      navigator.clipboard?.writeText(url);
    } else {
      window.open(links[where], "_blank", "noopener");
    }
    localStorage.removeItem("ms_spun");
    setCanSpin(true);
  };

  return (
    <motion.div
      data-testid="intro-screen"
      className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-black"
      exit={{ opacity: 0, transition: { duration: 1.1, ease: "easeInOut" } }}
    >
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute top-8 font-mono text-[11px] tracking-[0.5em] text-cream/70"
      >
        MARSTAR ORIGINALS
      </motion.p>

      {canSpin ? (
        <>
          <motion.div
            initial={{ opacity: 0, scale: 0.8, filter: "blur(14px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ duration: 2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <Compass selected={selected} locked={locked} onSelect={pick} />
          </motion.div>
          <motion.p
            data-testid="intro-hint"
            initial={{ opacity: 0 }}
            animate={{ opacity: locked ? 0 : 1 }}
            transition={{ delay: locked ? 0 : 2.4, duration: 1 }}
            className="hint-blink absolute bottom-10 px-6 text-center font-mono text-[11px] tracking-[0.35em] text-cream/70"
          >
            SPIN THE COMPASS — CHOOSE A DIRECTION FOR A DISCOUNT CODE
          </motion.p>
        </>
      ) : (
        <motion.div
          data-testid="share-panel"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative flex flex-col items-center px-6 text-center"
        >
          <div className="aura-strong absolute left-1/2 top-1/2 -z-10 aspect-square w-[min(80vw,420px)] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-70" />
          <img
            src="/assets/logo-mark.png"
            alt="MarStar Originals"
            className="w-[min(70vw,320px)]"
            style={{ filter: "drop-shadow(0 4px 30px rgba(43,47,217,0.45))" }}
          />
          <p className="mt-10 font-mono text-[10px] tracking-[0.45em] text-cream/60">
            YOUR STAR ALREADY SPOKE ON THIS DEVICE
          </p>
          <h2 className="mt-3 font-script text-6xl leading-none text-starlight">share the aura,</h2>
          <p className="mt-4 max-w-sm text-sm text-cream/70">
            Share MarStar with the world and the compass spins again.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {[
              { id: "x", label: "X" },
              { id: "facebook", label: "FACEBOOK" },
              { id: "whatsapp", label: "WHATSAPP" },
            ].map((b) => (
              <button
                key={b.id}
                type="button"
                data-testid={`share-${b.id}-button`}
                onClick={() => share(b.id)}
                className="flex items-center gap-2 border border-cream/25 px-5 py-3 font-mono text-[10px] tracking-[0.3em] text-cream/85 transition-colors hover:border-blush hover:text-blush"
              >
                <Share2 size={13} />
                {b.label}
              </button>
            ))}
            <button
              type="button"
              data-testid="share-copy-button"
              onClick={() => share("copy")}
              className="flex items-center gap-2 border border-cream/25 px-5 py-3 font-mono text-[10px] tracking-[0.3em] text-cream/85 transition-colors hover:border-blush hover:text-blush"
            >
              <Link2 size={13} />
              COPY LINK
            </button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
